import { type DataType as IufDataType, type IDataObject, type ISuggestion, queryBuilder } from '@inpart-io/devtools-iap';
import { type DataType, type IFieldCustomValue, privateFieldCustomValuesManager, sharedFieldCustomValuesManager } from '@inpart-io/padme-iap';
import { privateFieldCustomListValueProperties, sharedFieldCustomListValueProperties } from '@inpart-io/padme-iap';

import { useDataObjectHelper } from '@/composables/useDataObjectHelper';
import { LIMIT_TOTAL_DISPLAY } from '@/constants/field-custom-value.constants';

const { getDataObjectManager } = useDataObjectHelper();

const getDataObject = (suggestion: ISuggestion<IFieldCustomValue>): IFieldCustomValue => {
    return suggestion.dataObject;
};
export class TypeaheadService {
    public async doTypeaheadSharedSearch(text: string, listId: string, maxResults: number, defaultOrder: any[]): Promise<IFieldCustomValue[]> {
        try {
            const _operators = [];
            _operators.push(queryBuilder.cmp.equalTo('listId', listId));

            if (!text || text.trim().length < 1) {
                let displayLength = LIMIT_TOTAL_DISPLAY;
                const count = await sharedFieldCustomValuesManager.count(queryBuilder.build(queryBuilder.op.and(..._operators), 0, null, defaultOrder));
                if (count > LIMIT_TOTAL_DISPLAY) {
                    displayLength = maxResults;
                }
                const query = queryBuilder.build(queryBuilder.op.and(..._operators), 0, displayLength, defaultOrder);
                return await sharedFieldCustomValuesManager.search(query, { properties: sharedFieldCustomListValueProperties.DEFAULT });
            } else {
                const suggestions = await sharedFieldCustomValuesManager.typeahead(
                    text,
                    maxResults,
                    queryBuilder.op.and(..._operators),
                    privateFieldCustomListValueProperties.DEFAULT
                );
                return suggestions.filter(getDataObject).map(getDataObject);
            }
        } catch (error) {
            throw new Error(error.message);
        }
    }

    public async doTypeaheadPrivateSearch(text: string, listId: string, maxResults: number, defaultOrder: any[]): Promise<IFieldCustomValue[]> {
        try {
            const _operators = [];
            _operators.push(queryBuilder.cmp.equalTo('listId', listId));

            if (!text || text.trim().length < 1) {
                let displayLength = LIMIT_TOTAL_DISPLAY;
                const count = await privateFieldCustomValuesManager.count(queryBuilder.build(queryBuilder.op.and(..._operators), 0, null, defaultOrder));
                if (count > LIMIT_TOTAL_DISPLAY) {
                    displayLength = maxResults;
                }
                const query = queryBuilder.build(queryBuilder.op.and(..._operators), 0, displayLength, defaultOrder);
                return await privateFieldCustomValuesManager.search(query, { properties: privateFieldCustomListValueProperties.DEFAULT });
            } else {
                const suggestions = await privateFieldCustomValuesManager.typeahead(
                    text,
                    maxResults,
                    queryBuilder.op.and(..._operators),
                    privateFieldCustomListValueProperties.DEFAULT
                );
                return suggestions.filter(getDataObject).map(getDataObject);
            }
        } catch (error) {
            throw new Error(error.message);
        }
    }

    public async doTypeaheadSearch(
        type: DataType | IufDataType,
        text: string,
        maxResults: number,
        defaultOrder: any[],
        listId?: string,
        dobjIds?: number[],
        moreProperties?: string
    ): Promise<IDataObject[]> {
        try {
            const manager = getDataObjectManager(type);
            const properties =
                moreProperties?.length > 0 ? `name,label,dobjPrettifiedBusinessWiseId,${moreProperties}` : 'name,label,dobjPrettifiedBusinessWiseId';

            if (!text || text.trim().length < 1) {
                let operator = null;
                if (listId) {
                    operator = queryBuilder.op.and(queryBuilder.cmp.equalTo('listId', listId));
                }
                if (dobjIds?.length) {
                    operator = queryBuilder.op.and(queryBuilder.cmp.isIn('dobjId', dobjIds));
                }
                let displayLength = LIMIT_TOTAL_DISPLAY;
                const count = await manager.count(queryBuilder.build(operator, 0, null, defaultOrder));
                if (count > LIMIT_TOTAL_DISPLAY) {
                    displayLength = maxResults;
                }
                const query = queryBuilder.build(operator, 0, displayLength, defaultOrder);
                return await manager.search(query, { properties });
            } else {
                let operator = null;
                if (listId) {
                    operator = queryBuilder.op.and(queryBuilder.cmp.equalTo('listId', listId));
                }
                if (dobjIds?.length) {
                    operator = queryBuilder.op.and(queryBuilder.cmp.isIn('dobjId', dobjIds));
                }

                const suggestions = await manager.typeahead(text, maxResults, operator, properties);
                return suggestions.filter(getDataObject).map(getDataObject);
            }
        } catch (error) {
            console.error(error);
            throw error;
        }
    }
}

export const typeaheadService = new TypeaheadService();
